<template>
    <div class="event-management">
        <div class="header-section">
            <div class="header-left">
                <h2>Member Management</h2>
                <div class="filter-section">
                    <select v-model="statusFilter" class="status-filter">
                        <option value="active">Active Members</option>
                        <option value="all">All Members</option>
                        <option value="inactive">Inactive Members</option>
                    </select>
                </div>
            </div>
            <div class="header-actions">
                <button @click="emailAllMembers" class="email-btn">
                    Email All Members
                </button>
                <button @click="showAddUserModal = true" class="add-btn">
                    Add New Member
                </button>
            </div>
        </div>

        <div class="events-table">
            <table>
                <thead>
                    <tr>
                        <th @click="sortBy('name')" class="sortable">
                            Name & Email
                            <span class="sort-icon" v-if="sortField === 'name'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('company')" class="sortable">
                            Company
                            <span class="sort-icon" v-if="sortField === 'company'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th>Member Type</th>
                        <th class="actions-column">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in filteredAndSortedUsers" :key="user.id">
                        <td>
                            <div class="name-email" :class="{ 'inactive-user': user.status === 'inactive' }">
                                <div class="name">{{ user.first_name }} {{ user.last_name }}</div>
                                <div class="email">{{ user.email }}</div>
                            </div>
                        </td>
                        <td>{{ user.company_name || 'Not specified' }}</td>
                        <td>
                            <select 
                                v-model="user.role"
                                @change="handleRoleChange(user)"
                                class="role-select"
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                            </select>
                        </td>
                        <td>
                            <div class="action-menu-wrapper">
                                <button 
                                    class="action-menu-btn" 
                                    @click="toggleMenu(user.id)" 
                                    :data-user-id="user.id"
                                >
                                    <span class="material-icons">more_vert</span>
                                </button>
                                <div v-if="activeMenu === user.id" 
                                     class="action-menu"
                                     :ref="el => { if (el) actionMenus[user.id] = el }">
                                    <button @click="handleEdit(user.id)">Edit User</button>
                                    <button @click="handlePasswordReset(user)" class="password-reset">Send Password Reset</button>
                                    <button @click="handleDelete(user)" class="delete">
                                        {{ user.status === 'active' ? 'Deactivate User' : 'Activate User' }}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <AddUserModal 
            v-if="showAddUserModal"
            @close="showAddUserModal = false"
            @user-created="handleUserCreated"
        />

        <AdminEditUser
            v-if="showEditModal"
            :selected-user-id="selectedUserId"
            :selectedUser="selectedUser"
            @close="closeEditModal"
            @user-updated="handleUserUpdated"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed, nextTick, onUnmounted } from 'vue';
import AdminEditUser from '../components/AdminEditUser.vue';
import AddUserModal from '../components/AddUserModal.vue';
import { userService } from '../services/api';

export default {
    name: 'AdminUsers',
    components: {
        AdminEditUser,
        AddUserModal
    },
    setup() {
        const showEditModal = ref(false);
        const selectedUserId = ref(null);
        const selectedUser = ref(null);
        const users = ref([]);
        const showAddUserModal = ref(false);
        const activeMenu = ref(null);
        const actionMenus = ref({});
        const shouldFlipMenus = ref({});
        const sortField = ref('name');
        const sortDirection = ref('asc');
        const statusFilter = ref('active');

        const loadUsers = async () => {
            try {
                const response = await userService.getAllUsers();
                users.value = response;
            } catch (err) {
                console.error('Error loading users:', err);
            }
        };

        const confirmDelete = async (user) => {
            const isCurrentlyActive = user.status === 'active';
            const action = isCurrentlyActive ? 'deactivate' : 'activate';
            const newStatus = isCurrentlyActive ? 'inactive' : 'active';
            const message = isCurrentlyActive 
                ? `${user.first_name} ${user.last_name}? This will prevent them from logging in.`
                : `${user.first_name} ${user.last_name}? This will allow them to log in again.`;
            
            if (confirm(`Are you sure you want to ${action} ${message}`)) {
                try {
                    await userService.updateMemberStatus(user.id, newStatus, `${action === 'deactivate' ? 'Deactivated' : 'Activated'} by admin`);
                    await loadUsers();
                } catch (err) {
                    console.error(`Error ${action}ing user:`, err);
                    alert(err.response?.data?.error || `Failed to ${action} user`);
                }
            }
        };

        const openEditModal = (userId) => {
            const userToEdit = users.value.find(u => u.id === userId);
            if (!userToEdit) {
                console.error('User not found:', userId);
                return;
            }
            selectedUserId.value = userId.toString();
            selectedUser.value = userToEdit;
            showEditModal.value = true;
        };

        const closeEditModal = () => {
            // console.log('Closing edit modal');
            showEditModal.value = false;
            selectedUserId.value = null;
            selectedUser.value = null;
        };

        const handleUserUpdated = async (updatedUser) => {
            // Update the local user data
            const index = users.value.findIndex(u => u.id === updatedUser.id);
            if (index !== -1) {
                users.value[index] = { ...users.value[index], ...updatedUser };
            }
            // Refresh the user list
            await loadUsers();
            closeEditModal();
        };

        const handleUserCreated = () => {
            loadUsers();
            showAddUserModal.value = false;
        };

        const handleRoleChange = async (user) => {
            try {
                await userService.updateUserRole(user.id, user.role);
                await loadUsers();
                const updatedUser = users.value.find(u => u.id === user.id);
                
                if (updatedUser.role !== user.role) {
                    throw new Error('Role update failed to persist');
                }
            } catch (err) {
                console.error('Failed to update role:', err);
                await loadUsers();
            }
        };

        const toggleMenu = (userId) => {
            if (activeMenu.value === userId) {
                activeMenu.value = null;
                return;
            }

            activeMenu.value = userId;
            
            nextTick(() => {
                const menuElement = actionMenus.value[userId];
                if (menuElement) {
                    const buttonElement = document.querySelector(`[data-user-id="${userId}"]`);
                    if (buttonElement) {
                        const buttonRect = buttonElement.getBoundingClientRect();
                        menuElement.style.top = `${buttonRect.bottom}px`;
                        menuElement.style.left = `${buttonRect.left}px`;
                    }
                }
            });
        };

        const handleEdit = (userId) => {
            openEditModal(userId);
            activeMenu.value = null;
        };

        const handleDelete = (user) => {
            confirmDelete(user);
            activeMenu.value = null;
        };

        const handlePasswordReset = async (user) => {
            if (confirm(`Send password reset email to ${user.first_name} ${user.last_name} (${user.email})?`)) {
                try {
                    const response = await userService.sendPasswordReset(user.id);
                    alert(`✅ ${response.message}`);
                } catch (err) {
                    console.error('Error sending password reset:', err);
                    alert(`❌ Failed to send password reset: ${err.response?.data?.error || err.message}`);
                }
            }
            activeMenu.value = null;
        };

        const sortBy = (field) => {
            if (sortField.value === field) {
                // If clicking the same field, toggle direction
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                // If clicking a new field, set it and default to ascending
                sortField.value = field;
                sortDirection.value = 'asc';
            }
        };

        const filteredAndSortedUsers = computed(() => {
            let filteredUsers = users.value;

            if (statusFilter.value === 'active') {
                filteredUsers = filteredUsers.filter(u => u.status === 'active');
            } else if (statusFilter.value === 'inactive') {
                filteredUsers = filteredUsers.filter(u => u.status === 'inactive');
            }

            return [...filteredUsers].sort((a, b) => {
                let compareA, compareB;
                
                if (sortField.value === 'name') {
                    compareA = `${a.first_name} ${a.last_name}`.toLowerCase();
                    compareB = `${b.first_name} ${b.last_name}`.toLowerCase();
                } else if (sortField.value === 'company') {
                    compareA = (a.company_name || '').toLowerCase();
                    compareB = (b.company_name || '').toLowerCase();
                }

                if (sortDirection.value === 'asc') {
                    return compareA > compareB ? 1 : -1;
                } else {
                    return compareA < compareB ? 1 : -1;
                }
            });
        });

        onMounted(() => {
            loadUsers();
        });

        // Email all active members function
        const emailAllMembers = async () => {
            // Get active members only, excluding visitors
            const activeMembers = users.value.filter(user => 
                user.status === 'active' && 
                user.role !== 'visitor' &&
                user.email
            );

            if (activeMembers.length === 0) {
                alert('No active members found to email.');
                return;
            }

            const emails = activeMembers.map(user => user.email).join(',');
            
            // Copy to clipboard with fallback
            let clipboardSuccess = false;
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(emails);
                    clipboardSuccess = true;
                    console.log('Emails copied to clipboard');
                } else {
                    // Fallback for older browsers or non-HTTPS
                    const textArea = document.createElement('textarea');
                    textArea.value = emails;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    clipboardSuccess = true;
                    console.log('Emails copied to clipboard (fallback method)');
                }
            } catch (err) {
                console.error('Could not copy emails to clipboard:', err);
                clipboardSuccess = false;
            }

            // Create mailto link with BCC and default content
            const subject = encodeURIComponent('Important Update from Action Network');
            const body = encodeURIComponent(`Hi everyone,

We have an important update regarding our Action Network login system.

[Your message here]

Best regards,
Action Network Admin`);
            
            // Give user choice between Gmail (browser) or default email app
            const useGmail = confirm('Open in Gmail (browser) or default email app?\n\nOK = Gmail in browser\nCancel = Default email app');
            
            if (useGmail) {
                // Open Gmail compose in browser
                const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(emails)}&subject=${subject}&body=${body}`;
                window.open(gmailUrl, '_blank');
            } else {
                // Use traditional mailto (default email app)
                const mailtoLink = `mailto:?bcc=${emails}&subject=${subject}&body=${body}`;
                const tempLink = document.createElement('a');
                tempLink.href = mailtoLink;
                tempLink.target = '_blank';
                document.body.appendChild(tempLink);
                tempLink.click();
                document.body.removeChild(tempLink);
            }
            
            // Show success message
            setTimeout(() => {
                const clipboardMsg = clipboardSuccess 
                    ? 'Emails also copied to clipboard as backup.'
                    : 'Could not copy emails to clipboard - email client should still work.';
                alert(`Opening email client with ${activeMembers.length} active members in BCC. ${clipboardMsg}`);
            }, 100);
        };

        // Clear refs when component unmounts
        onUnmounted(() => {
            actionMenus.value = {};
            shouldFlipMenus.value = {};
        });

        return {
            showEditModal,
            selectedUserId,
            selectedUser,
            openEditModal,
            closeEditModal,
            handleUserUpdated,
            users,
            showAddUserModal,
            handleUserCreated,
            confirmDelete,
            handleRoleChange,
            activeMenu,
            actionMenus,
            shouldFlipMenus,
            toggleMenu,
            handleEdit,
            handleDelete,
            handlePasswordReset,
            sortField,
            sortDirection,
            sortBy,
            statusFilter,
            filteredAndSortedUsers,
            emailAllMembers
        };
    }
};
</script>

<style scoped>
.event-management {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.filter-section {
    margin-left: 2rem;
    display: inline-flex;
    align-items: center;
}

.status-filter {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
    min-width: 150px;
    font-size: 0.9rem;
}

.header-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.email-btn {
    background-color: #2196F3;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    font-size: 0.9rem;
}

.email-btn:hover {
    background-color: #1976D2;
}

.add-btn {
    background-color: #4CAF50;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    font-size: 0.9rem;
}

.add-btn:hover {
    background-color: #45a049;
}

.events-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: hidden;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th {
    text-align: left;
    padding: 1rem;
    background: #f8f9fa;
    font-weight: 500;
    color: #2c3e50;
    border-bottom: 2px solid #eee;
}

td {
    padding: 1rem;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
    text-align: left;
}

tr:last-child td {
    border-bottom: none;
}

.name-email {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.name {
    font-weight: 500;
    color: #333;
}

.email {
    color: #666;
    font-size: 0.9rem;
}

.inactive-user, .inactive-user .name, .inactive-user .email {
    color: #dc3545;
    font-style: italic;
}

.inactive-user .email {
    opacity: 0.8;
}

.role-select {
    padding: 0.25rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
}

.actions-column {
    width: 100px;
}

.dropdown {
    position: relative;
}

.dropdown-menu {
    position: fixed;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    min-width: 160px;
    right: 20px;
    z-index: 9999;
}

.dropdown-item {
    padding: 8px 16px;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
}

.dropdown-item:hover {
    background-color: #f5f5f5;
}

.btn-icon {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
}

.material-icons {
    color: #666;
    font-size: 20px;
}

.sortable {
    cursor: pointer;
    user-select: none;
}

.sortable:hover {
    background-color: #f0f4f8;
}

.sort-icon {
    margin-left: 0.5rem;
    font-size: 0.8rem;
}

.actions-cell {
    position: relative;
}

.action-menu-wrapper {
    position: relative;
}

.action-menu {
    position: fixed;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    min-width: 160px;
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.action-menu button {
    width: 100%;
    text-align: left;
    padding: 8px 16px;
    border: none;
    background: none;
    cursor: pointer;
    color: #2c3e50;
    font-size: 0.9rem;
}

.action-menu button:hover {
    background-color: #f5f5f5;
}

.action-menu button.delete {
    color: #dc3545;
}

.action-menu button.delete:hover {
    background-color: #ffebee;
}

.action-menu button.password-reset {
    color: #007bff;
}

.action-menu button.password-reset:hover {
    background-color: #e3f2fd;
}
</style>